import { apiService } from './apiService';
import {
  RoomOrientation,
  SeatLayout,
  SeatRow,
  SeatPosition,
  SlotState,
  makeEmptyLayout,
} from '../types/seatLayout';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  SEAT LAYOUT STORE — the API boundary, and the only place shapes are mapped
 * ─────────────────────────────────────────────────────────────────────────────
 * The backend and the editor deliberately speak different dialects:
 *
 *   backend   rows[].seats[]   state: 'b' | 'x' | '-'                number
 *   editor    rows[].seats[]   state: 'bookable' | 'oos' | 'gap'     label
 *
 * One-character states keep the stored document small — they repeat once per
 * seat in every room and every response. Spelled-out ones keep the editor
 * readable, where `state === 'bookable'` beats `state === 'b'` in twenty places.
 *
 * Rather than compromise either, the translation lives HERE, at the single point
 * data crosses. No component knows the wire format, and no stored document
 * carries UI vocabulary.
 *
 *   list   -> GET    /api/v1/seat-layouts          (or /admin/studio/:id)
 *   create -> POST   /api/v1/seat-layouts
 *   update -> PATCH  /api/v1/seat-layouts/:id
 *   remove -> DELETE /api/v1/seat-layouts/:id      (soft delete)
 *   copy   -> POST   /api/v1/seat-layouts/:id/duplicate
 */

/* ── Wire ↔ editor mapping ─────────────────────────────────────────────────── */

const TO_WIRE: Record<SlotState, string> = { bookable: 'b', oos: 'x', gap: '-' };
const FROM_WIRE: Record<string, SlotState> = { b: 'bookable', x: 'oos', '-': 'gap' };

/**
 * Client-generated ids (`slot_…`) are omitted so the SERVER mints identity from
 * the start. Sending one would just be re-minted anyway, and a locally-invented
 * id that survived into a booking would be a seat nobody can trace.
 */
const seatToWire = (seat: SeatPosition) => ({
  ...(seat.seatId && !seat.seatId.startsWith('slot_') ? { seatId: seat.seatId } : {}),
  state: TO_WIRE[seat.state],
  ...(seat.state !== 'gap' && seat.label ? { number: seat.label } : {}),
  ...(seat.isAccessible ? { isAccessible: true } : {}),
  ...(seat.state === 'oos' && seat.oosReason ? { oosReason: seat.oosReason } : {}),
  ...(seat.state === 'oos' && seat.oosExpectedBackAt
    ? { oosExpectedBackAt: new Date(seat.oosExpectedBackAt).toISOString() }
    : {}),
});

const rowToWire = (row: SeatRow) => ({
  ...(row.rowId && !row.rowId.startsWith('row_') ? { rowId: row.rowId } : {}),
  label: row.label,
  surcharge: row.surcharge ?? 0,
  seats: row.seats.map(seatToWire),
});

const seatFromWire = (seat: any): SeatPosition => ({
  seatId: seat.seatId,
  state: FROM_WIRE[seat.state] ?? 'bookable',
  label: seat.number ?? null,
  isAccessible: seat.isAccessible ?? false,
  oosReason: seat.oosReason ?? null,
  // The editor's date input wants YYYY-MM-DD, not an ISO timestamp.
  oosExpectedBackAt: seat.oosExpectedBackAt
    ? String(seat.oosExpectedBackAt).slice(0, 10)
    : null,
});

const layoutFromWire = (doc: any): SeatLayout => ({
  _id: doc._id,
  studio: typeof doc.studio === 'string' ? doc.studio : doc.studio?._id,
  studioName: doc.studio?.studioName,
  name: doc.name,
  description: doc.description ?? '',
  rows: (doc.rows ?? []).map((r: any) => ({
    rowId: r.rowId,
    label: r.label,
    surcharge: r.surcharge ?? 0,
    seats: (r.seats ?? []).map(seatFromWire),
  })),
  autoNumber: doc.autoNumber ?? true,
  orientation: (doc.orientation ?? 'instructor_top') as RoomOrientation,
  frontLabel: doc.frontLabel ?? 'INSTRUCTOR',
  backLabel: doc.backLabel ?? 'ENTRANCE',
  bookableCount: doc.bookableCount ?? 0,
  version: doc.version ?? 1,
  createdAt: doc.createdAt,
  updatedAt: doc.updatedAt,
});

const layoutToWire = (draft: any) => ({
  name: draft.name?.trim(),
  description: draft.description?.trim() || undefined,
  rows: (draft.rows ?? []).map(rowToWire),
  autoNumber: draft.autoNumber ?? true,
  orientation: draft.orientation,
  frontLabel: draft.frontLabel,
  backLabel: draft.backLabel,
});

/** This API returns several envelope shapes depending on the route. */
const unwrapList = (res: any): any[] =>
  res?.seatLayouts ?? res?.data?.seatLayouts ?? res?.data ?? [];
const unwrapOne = (res: any): any =>
  res?.seatLayout ?? res?.data?.seatLayout ?? res?.data ?? res;

/* ── Store ─────────────────────────────────────────────────────────────────── */

export const seatLayoutStore = {
  async list(studioId: string): Promise<SeatLayout[]> {
    if (!studioId) return [];
    const res = await apiService.getSeatLayouts(studioId);
    return unwrapList(res).map(layoutFromWire);
  },

  async get(studioId: string, id: string): Promise<SeatLayout | null> {
    const all = await this.list(studioId);
    return all.find((l) => l._id === id) ?? null;
  },

  async create(draft: any): Promise<SeatLayout> {
    const res = await apiService.createSeatLayout(draft.studio, layoutToWire(draft));
    return layoutFromWire(unwrapOne(res));
  },

  // studioId pins each write to the room's location — under "All locations"
  // the switcher sends __all__, which the server refuses for writes.
  async update(studioId: string, id: string, patch: any): Promise<SeatLayout> {
    const res = await apiService.updateSeatLayout(id, layoutToWire(patch), studioId);
    return layoutFromWire(unwrapOne(res));
  },

  async remove(studioId: string, id: string): Promise<void> {
    await apiService.deleteSeatLayout(id, studioId);
  },

  async duplicate(studioId: string, id: string): Promise<SeatLayout> {
    const res = await apiService.duplicateSeatLayout(id, studioId);
    return layoutFromWire(unwrapOne(res));
  },

  /** Blank draft for the "new room" flow. */
  draft(studioId: string, studioName?: string) {
    return makeEmptyLayout(studioId, studioName);
  },
};

export default seatLayoutStore;
